import React from 'react';
import { IMaskInput } from 'react-imask';

export interface FieldInputProps {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  required?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;

  mask?: string | RegExp | NumberConstructor | DateConstructor | Record<string, unknown>;
  unmask?: boolean | 'typed';
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
}

export const FieldInput: React.FC<FieldInputProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  icon,
  className,
  mask,
  unmask = false,
  inputMode,
}) => {
  const inputClass = [
    'w-full rounded-md border px-3 py-2 shadow-sm',
    'focus:outline-none focus:ring-2',
    error
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500'
      : 'border-gray-300 focus:ring-blue-500',
    icon ? 'pl-10' : '',
    disabled ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-600' : '',
  ]
    .join(' ')
    .trim();

  const errorId = error ? `${label.replace(/\s+/g, '-').toLowerCase()}-error` : undefined;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 sm:text-sm">{icon}</span>
          </div>
        )}

        {mask ? (
          <IMaskInput
            mask={mask as any}
            value={value || ''}
            unmask={unmask}
            onAccept={(val) => onChange?.(String(val))}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClass}
            inputMode={inputMode}
            type={type}
            aria-invalid={!!error}
            aria-describedby={errorId}
          />
        ) : (
          <input
            type={type}
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClass}
            inputMode={inputMode}
            aria-invalid={!!error}
            aria-describedby={errorId}
          />
        )}

        {error && (
          <div id={errorId} className="mt-1 text-xs text-red-600">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};